import React, { useMemo } from 'react';
import './MarkdownRenderer.css';

interface MarkdownRendererProps {
  content: string;
}

function renderMarkdown(text: string): React.ReactNode[] {
    const lines = text.split('\n');
    const elements: React.ReactNode[] = [];
    let inCodeBlock = false;
    let codeContent: string[] = [];
    let inTable = false;
    let tableRows: string[][] = [];
    let tableHeaders: string[] = [];

    const processInlineFormatting = (line: string): React.ReactNode => {
      const parts: React.ReactNode[] = [];
      let remaining = line;
      let key = 0;

      while (remaining.length > 0) {
        const boldMatch = remaining.match(/\*\*(.+?)\*\*/);
        const codeMatch = remaining.match(/`([^`]+)`/);

        if (boldMatch && (!codeMatch || boldMatch.index! <= codeMatch.index!)) {
          if (boldMatch.index! > 0) {
            parts.push(<span key={key++}>{remaining.slice(0, boldMatch.index)}</span>);
          }
          parts.push(<strong key={key++}>{boldMatch[1]}</strong>);
          remaining = remaining.slice(boldMatch.index! + boldMatch[0].length);
        } else if (codeMatch) {
          if (codeMatch.index! > 0) {
            parts.push(<span key={key++}>{remaining.slice(0, codeMatch.index)}</span>);
          }
          parts.push(<code key={key++} className="md-inline-code">{codeMatch[1]}</code>);
          remaining = remaining.slice(codeMatch.index! + codeMatch[0].length);
        } else {
          parts.push(<span key={key++}>{remaining}</span>);
          break;
        }
      }

      return parts.length === 1 ? parts[0] : <>{parts}</>;
    };

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      const trimmedLine = line.trim();

      if (trimmedLine.startsWith('```')) {
        if (inCodeBlock) {
          elements.push(
            <pre key={i} className="md-code-block">
              <code>{codeContent.join('\n')}</code>
            </pre>
          );
          codeContent = [];
          inCodeBlock = false;
        } else {
          inCodeBlock = true;
        }
        continue;
      }

      if (inCodeBlock) {
        codeContent.push(line);
        continue;
      }

      if (trimmedLine.startsWith('|') && trimmedLine.endsWith('|')) {
        const cells = trimmedLine.slice(1, -1).split('|').map((c) => c.trim());

        if (!inTable) {
          inTable = true;
          tableHeaders = cells;
        } else if (cells.every((c) => /^[-:]+$/.test(c))) {
          continue;
        } else {
          tableRows.push(cells);
        }
        continue;
      } else if (inTable) {
        elements.push(
          <table key={i} className="md-table">
            <thead>
              <tr>
                {tableHeaders.map((h, j) => <th key={j}>{processInlineFormatting(h)}</th>)}
              </tr>
            </thead>
            <tbody>
              {tableRows.map((row, j) => (
                <tr key={j}>
                  {row.map((cell, k) => <td key={k}>{processInlineFormatting(cell)}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        );
        inTable = false;
        tableHeaders = [];
        tableRows = [];
      }

      if (!trimmedLine) {
        continue;
      }

      if (trimmedLine.startsWith('### ')) {
        elements.push(<h4 key={i} className="md-h4">{processInlineFormatting(trimmedLine.slice(4))}</h4>);
        continue;
      }
      if (trimmedLine.startsWith('## ')) {
        elements.push(<h3 key={i} className="md-h3">{processInlineFormatting(trimmedLine.slice(3))}</h3>);
        continue;
      }
      if (trimmedLine.startsWith('# ')) {
        elements.push(<h2 key={i} className="md-h2">{processInlineFormatting(trimmedLine.slice(2))}</h2>);
        continue;
      }

      if (trimmedLine === '---') {
        elements.push(<hr key={i} className="md-hr" />);
        continue;
      }

      if (trimmedLine.startsWith('- ')) {
        elements.push(
          <p key={i} className="md-list-item">{processInlineFormatting(trimmedLine.slice(2))}</p>
        );
        continue;
      }

      const numberedMatch = trimmedLine.match(/^(\d+)\.\s+(.+)$/);
      if (numberedMatch) {
        elements.push(
          <p key={i} className="md-list-item md-list-item--numbered">
            <span className="md-list-number">{numberedMatch[1]}.</span>
            {processInlineFormatting(numberedMatch[2])}
          </p>
        );
        continue;
      }

      elements.push(<p key={i} className="md-paragraph">{processInlineFormatting(trimmedLine)}</p>);
    }

    if (inCodeBlock) {
      for (let i = 0; i < codeContent.length; i++) {
        const leftover = codeContent[i];
        if (!leftover.trim()) continue;
        elements.push(
          <p key={`unclosed-fence-${i}`} className="md-paragraph">
            {processInlineFormatting(leftover)}
          </p>
        );
      }
    }

    if (inTable && tableHeaders.length > 0) {
      elements.push(
        <table key="final-table" className="md-table">
          <thead>
            <tr>
              {tableHeaders.map((h, j) => <th key={j}>{processInlineFormatting(h)}</th>)}
            </tr>
          </thead>
          <tbody>
            {tableRows.map((row, j) => (
              <tr key={j}>
                {row.map((cell, k) => <td key={k}>{processInlineFormatting(cell)}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      );
    }

    return elements;
}

export const MarkdownRenderer: React.FC<MarkdownRendererProps> = ({ content }) => {
  const nodes = useMemo(() => renderMarkdown(content), [content]);
  return <div className="md-markdown">{nodes}</div>;
};

export default MarkdownRenderer;
